import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  ClipboardList, 
  RefreshCw, 
  MessageCircle, 
  CheckCircle2,
  AlertCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';
import { colorTokens } from '@/common/styles/colorTokens';
import Avatar from '@/common/components/avatar/Avatar';

// 알림 타입 정의
export enum NotificationType {
  NOTIFICATION = 'NOTIFICATION',  
  TASK = 'TASK',
  READONLY = 'READONLY',
}

// 알림 인터페이스 (common용 간소화 버전)
export interface NotificationData {
  id: string;
  type: NotificationType;
  status: { id: number; name: string };
  sender?: { id: number; name: string; profileImage?: string };
  message: string;
  description?: string;
  createdAt: string;
  url?: string | null;
}

interface NotificationItemProps {
  notification: NotificationData;
  onRead: (notification: NotificationData) => void;
  animationDelay?: number;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onRead,
  animationDelay = 0
}) => {
  const isReadOnly = notification.type === NotificationType.READONLY;

  // 상태별 정보
  const getStatusInfo = () => {
    const statusId = notification.status.id;
    
    switch (statusId) {
      case 1:
        return { text: '미확인', color: 'red', isUnread: true, icon: AlertCircle };
      case 2:
        return { text: '확인됨', color: 'gray', isUnread: false, icon: CheckCircle2 };
      case 3:
        return { text: '진행중', color: 'blue', isUnread: false, icon: Loader2 };
      case 4:
        return { text: '완료', color: 'green', isUnread: false, icon: CheckCircle2 };
      case 5:
        return { text: '취소', color: 'gray', isUnread: false, icon: XCircle };
      default:
        return { text: '알 수 없음', color: 'gray', isUnread: false, icon: AlertCircle };
    }
  };

  // 타입별 설정
  const getTypeConfig = () => {
    switch (notification.type) {
      case NotificationType.TASK:
        return { 
          icon: ClipboardList, 
          label: '할일',
          color: colorTokens.orange[500],
          lightColor: colorTokens.orange[100],
        };
      case NotificationType.NOTIFICATION:
        return { 
          icon: RefreshCw, 
          label: '업데이트',
          color: colorTokens.blue[500],
          lightColor: colorTokens.blue[100],
        };
      case NotificationType.READONLY:
        return { 
          icon: MessageCircle, 
          label: '메세지',
          color: colorTokens.purple[500],
          lightColor: colorTokens.purple[100],
        };
      default:
        return { 
          icon: MessageCircle, 
          label: '알림',
          color: colorTokens.gray[500],
          lightColor: colorTokens.gray[100],
        };
    }
  };

  const statusInfo = getStatusInfo();
  const typeConfig = getTypeConfig();
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusInfo.icon;

  const handleClick = () => {
    if (isReadOnly) return;
    onRead(notification);
  };

  const formatTime = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { locale: ko, addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <Container 
      onClick={handleClick} 
      $isUnread={statusInfo.isUnread}
      $type={notification.type}
      $isClickable={!isReadOnly}
      $typeColor={typeConfig.color}
      style={{ animationDelay: `${animationDelay}s` }}
    >
      {statusInfo.isUnread && <UnreadDot $color={typeConfig.color} />}
      
      <MainContent>
        <TopSection>
          <SenderArea>
            {notification.sender ? (
              <AvatarWrapper>
                <Avatar 
                  src={notification.sender.profileImage} 
                  alt={notification.sender.name || ''} 
                  sizes='md'
                />
                <TypeIconBadge $color={typeConfig.color}>
                  <TypeIcon size={8} />
                </TypeIconBadge>
              </AvatarWrapper>
            ) : (
              <SystemIcon $color={typeConfig.color} $lightColor={typeConfig.lightColor}>
                <TypeIcon size={18} />
              </SystemIcon>
            )}
            
            <SenderInfo>
              <SenderName $isUnread={statusInfo.isUnread}>
                {notification.sender?.name || '시스템'}
              </SenderName>
              <MetaRow>
                <TypeLabel $color={typeConfig.color}>
                  {typeConfig.label}
                </TypeLabel>
                <MetaDivider>•</MetaDivider>
                <TimeStamp>
                  {formatTime(notification.createdAt)}
                </TimeStamp>
              </MetaRow>
            </SenderInfo>
          </SenderArea>
          
          {!isReadOnly && (
            <StatusBadge $color={statusInfo.color}>
              <StatusIcon size={10} />
              {statusInfo.text}
            </StatusBadge>
          )}
        </TopSection>
        
        <MessageArea>
          <Message $isUnread={statusInfo.isUnread}>
            {notification.message}
          </Message>
          
          {notification.description && (
            <DescriptionCard $color={typeConfig.color}>
              <DescriptionText>{notification.description}</DescriptionText>
            </DescriptionCard>
          )}
        </MessageArea>
      </MainContent>
    </Container>
  );
};

const fadeInSlide = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const Container = styled.div<{ 
  $isUnread: boolean; 
  $type: NotificationType;
  $isClickable: boolean;
  $typeColor: string;
}>`
  position: relative;
  padding: 14px 18px;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  background: ${props => props.$isUnread ? `${props.$typeColor}08` : 'white'};
  border-bottom: 1px solid ${colorTokens.gray[100]};
  transition: all 0.15s ease;
  animation: ${fadeInSlide} 0.25s ease-out both;
  
  ${props => props.$isClickable && css`
    &:hover {
      background: ${props.$isUnread ? `${props.$typeColor}12` : colorTokens.gray[50]};
    }
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const UnreadDot = styled.div<{ $color: string }>`
  position: absolute;
  top: 18px;
  left: 8px;
  width: 6px;
  height: 6px;
  background: ${props => props.$color};
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 6px;
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const SenderArea = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const TypeIconBadge = styled.div<{ $color: string }>`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: ${props => props.$color};
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const SystemIcon = styled.div<{ $color: string; $lightColor: string }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${props => props.$lightColor};
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SenderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SenderName = styled.span<{ $isUnread: boolean }>`
  font-size: 0.875rem;
  font-weight: ${props => props.$isUnread ? 600 : 500};
  color: ${colorTokens.gray[900]};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TypeLabel = styled.span<{ $color: string }>`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${props => props.$color};
`;

const MetaDivider = styled.span`
  color: ${colorTokens.gray[300]};
  font-size: 0.625rem;
`;

const TimeStamp = styled.span`
  font-size: 0.6875rem;
  color: ${colorTokens.gray[800]};
`;

const StatusBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$color) {
      case 'red': return colorTokens.red[100];
      case 'blue': return colorTokens.blue[100];
      case 'green': return colorTokens.green[100];
      default: return colorTokens.gray[100];
    }
  }};
  color: ${props => {
    switch (props.$color) {
      case 'red': return colorTokens.red[600];
      case 'blue': return colorTokens.blue[600];
      case 'green': return colorTokens.green[600];
      default: return colorTokens.gray[500];
    }
  }};
`;

const MessageArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Message = styled.p<{ $isUnread: boolean }>`
  font-size: 0.8125rem;
  font-weight: ${props => props.$isUnread ? 500 : 400};
  color: ${colorTokens.gray[800]};
  margin: 0;
  line-height: 1.5;
  word-break: keep-all;
`;

const DescriptionCard = styled.div<{ $color: string }>`
  background: ${colorTokens.gray[100]};
  border-left: 3px solid ${props => props.$color};
  border-radius: 0 8px 8px 0;
  padding: 10px 12px;
`;

const DescriptionText = styled.p`
  font-size: 0.75rem;
  color: ${colorTokens.gray[800]};
  margin: 0;
  line-height: 1.5;
`;

export default NotificationItem;

